/**
 * Every change to the credit on ONE order — redeeming a gift-card code,
 * applying, removing, auto-applying — and the payment that must not overtake
 * them, coordinated in one place.
 *
 * Module-level, keyed by order id, and deliberately NOT component state: the
 * delivery step unmounts on Back and mounts again on Forward, and a redemption
 * started on the first mount keeps running after it. Kept in a component, the
 * second mount would start with "nothing running", let the customer untick,
 * and then watch the old redemption apply credit over that choice.
 *
 * Three rules, each a race the checkout used to lose:
 *
 *  1. Serialized. One operation per order at a time; a new one waits for the
 *     one running. A gift-card redemption started while an auto-apply is still
 *     in flight used to be decided against the order as it was BEFORE that
 *     apply landed, and then skipped its own top-up — the card was claimed,
 *     the field said "added", and the order was never topped up.
 *
 *  2. The customer's latest explicit choice wins. Every tick or untick bumps a
 *     per-order revision (`recordChoice`); anything the checkout does on its
 *     own — an auto top-up, the apply after a redemption — captures the
 *     revision when it is requested and is dropped if a newer choice was made
 *     before it ran.
 *
 *  3. Payment waits. `beginPayment` is a synchronous lock taken the moment pay
 *     is pressed (before the form's async validation), after which no new
 *     credit operation starts; `settle` then waits for whatever was already
 *     running or queued, and says whether any of it failed, so a customer who
 *     pressed Apply and then Pay is never sent to Stripe for the full amount.
 *
 * Pure (no React, no API), so the interleavings are tested with deferred
 * promises in `credit-operations.test.ts`. `useOrderCredit` and the delivery
 * step read it through `useSyncExternalStore`.
 */

type Operation = {
  run: () => Promise<boolean>;
  /** Operations with the same key, queued back to back, run once. */
  coalesce?: string;
  started: boolean;
  promise: Promise<boolean>;
  resolve: (ok: boolean) => void;
};

type OrderOperations = {
  /** The running operation (if any) first, then the queued ones in order. */
  queue: Operation[];
  /** Bumped by every explicit tick / untick. */
  intent: number;
  paying: boolean;
  /** How many operations have failed so far; `settle` compares against it. */
  failures: number;
  idleWaiters: Array<() => void>;
};

/** Handed out by `beginPayment`; failures before it are not payment's concern. */
export type PaymentTicket = { failuresBefore: number };

export type EnqueueOptions = {
  /**
   * An explicit customer choice (tick / untick): recorded as the newest
   * intent, which makes any automatic apply still waiting obsolete.
   */
  choice?: boolean;
  /** See `Operation.coalesce`. */
  coalesce?: string;
};

export function createCreditOperations() {
  const orders = new Map<string, OrderOperations>();
  const listeners = new Set<() => void>();

  const stateOf = (orderId: string): OrderOperations => {
    let state = orders.get(orderId);
    if (!state) {
      state = { queue: [], intent: 0, paying: false, failures: 0, idleWaiters: [] };
      orders.set(orderId, state);
    }
    return state;
  };
  const notify = () => listeners.forEach((listener) => listener());

  const pump = (orderId: string) => {
    const state = stateOf(orderId);
    const head = state.queue[0];
    if (!head || head.started) return;
    head.started = true;
    void (async () => {
      let ok = false;
      try {
        ok = await head.run();
      } catch {
        ok = false;
      }
      if (!ok) state.failures += 1;
      state.queue.shift();
      head.resolve(ok);
      if (state.queue.length === 0) {
        state.idleWaiters.splice(0).forEach((wake) => wake());
      }
      notify();
      pump(orderId);
    })();
  };

  return {
    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    /** An operation is running or queued for this order. */
    isBusy: (orderId: string): boolean => (orders.get(orderId)?.queue.length ?? 0) > 0,

    /** Payment has begun for this order: credit can no longer change. */
    isPaying: (orderId: string): boolean => orders.get(orderId)?.paying ?? false,

    /** The current revision of the customer's explicit choice. */
    intent: (orderId: string): number => orders.get(orderId)?.intent ?? 0,

    /** Records an explicit tick / untick; see rule 2. */
    recordChoice(orderId: string): number {
      const state = stateOf(orderId);
      state.intent += 1;
      return state.intent;
    },

    /**
     * Runs `run` after every operation already queued for this order.
     *
     * @returns whether it succeeded (a throw is a failure), or null when it
     *          was refused because payment has begun.
     */
    enqueue(
      orderId: string,
      run: () => Promise<boolean>,
      options: EnqueueOptions = {},
    ): Promise<boolean> | null {
      const state = stateOf(orderId);
      if (state.paying) return null;
      if (options.choice) state.intent += 1;

      const tail = state.queue[state.queue.length - 1];
      if (options.coalesce && tail && !tail.started && tail.coalesce === options.coalesce) {
        return tail.promise;
      }

      let resolve!: (ok: boolean) => void;
      const promise = new Promise<boolean>((r) => {
        resolve = r;
      });
      state.queue.push({ run, coalesce: options.coalesce, started: false, promise, resolve });
      notify();
      pump(orderId);
      return promise;
    },

    /**
     * The synchronous payment lock. Null when a payment is already under way
     * for this order — a second press must do nothing.
     */
    beginPayment(orderId: string): PaymentTicket | null {
      const state = stateOf(orderId);
      if (state.paying) return null;
      state.paying = true;
      notify();
      return { failuresBefore: state.failures };
    },

    endPayment(orderId: string): void {
      const state = orders.get(orderId);
      if (!state?.paying) return;
      state.paying = false;
      notify();
    },

    /**
     * Resolves once nothing is running or queued for this order, with
     * whether any operation failed since `ticket` was taken — the customer
     * then has to see why before being charged.
     */
    async settle(orderId: string, ticket: PaymentTicket): Promise<{ failed: boolean }> {
      const state = stateOf(orderId);
      if (state.queue.length > 0) {
        await new Promise<void>((wake) => state.idleWaiters.push(wake));
      }
      return { failed: state.failures > ticket.failuresBefore };
    },
  };
}

export type CreditOperations = ReturnType<typeof createCreditOperations>;

/** The one coordinator every mount of the checkout shares. */
export const creditOperations = createCreditOperations();

/**
 * Applying credit because the checkout decided to (auto-apply), not because
 * the customer asked: queued once however often it is requested while busy,
 * and dropped if the customer makes an explicit choice before it runs.
 */
export function requestTopUp(
  ops: CreditOperations,
  orderId: string,
  apply: () => Promise<boolean>,
): Promise<boolean> | null {
  const intentAtRequest = ops.intent(orderId);
  return ops.enqueue(
    orderId,
    async () => (ops.intent(orderId) === intentAtRequest ? apply() : true),
    { coalesce: "top-up" },
  );
}

/**
 * "Imam darilni bon" as ONE operation: redeem the code, then put the money on
 * the order — whatever the box showed when the code was typed, since typing
 * a code is itself the request to use it — unless the customer made a newer
 * explicit choice while the redemption ran (rule 2).
 *
 * @returns `redeemed`, which settles only after the whole transaction (so the
 *          field stays busy through the apply), or null when refused because
 *          payment has begun.
 */
export function redeemAndApply(
  ops: CreditOperations,
  orderId: string,
  steps: {
    /** Trades the code for a claim token; whether it was redeemed. */
    redeem: () => Promise<boolean>;
    /** Runs after a successful redemption whatever happens next. */
    onRedeemed: () => void;
    /** Clears a stored opt-out; runs only when the apply will. */
    optIn: () => void;
    /** Applies the order's credit now; whether it succeeded. */
    apply: () => Promise<boolean>;
  },
): { redeemed: Promise<boolean> } | null {
  const intentAtRequest = ops.intent(orderId);
  let redeemed = false;
  const done = ops.enqueue(orderId, async () => {
    redeemed = await steps.redeem();
    if (!redeemed) return false;
    steps.onRedeemed();
    if (ops.intent(orderId) !== intentAtRequest) return true;
    steps.optIn();
    return steps.apply();
  });
  if (!done) return null;
  return { redeemed: done.then(() => redeemed) };
}
